/**
 * No-access / profile-load-failure screen — rendered when GET /me fails. A valid Auth0 token can still
 * be 403ed by the backend Stage-2 roster gate (the user authenticated but isn't on the staff roster),
 * so we MUST distinguish that from a generic transport/server error and never fall through to a blank
 * crash:
 *
 *   403 -> "not on the staff roster" + sign out (so they can try another account)
 *   0   -> network error + retry
 *   else -> generic error + retry
 *
 * Sign-out is offered on the 403 because retrying the same token will just 403 again — the fix is a
 * different identity, not a refetch.
 */

import { useTranslation } from "react-i18next";
import { ShieldOff } from "lucide-react";
import { ApiRequestError } from "@/api/client";
import { signalSessionExpired } from "@/context/sessionExpiredSignal";
import { Button } from "@/components/ui";

export interface NoAccessProps {
  error: unknown;
  /** Refetch /me (shown for transient/network errors, NOT for a 403 or a 401). */
  onRetry?: () => void;
  /** Sign out so the user can try a different account (shown for a 403). */
  onSignOut?: () => void;
  /** Re-authenticate (shown for a 401 — an expired session). Defaults to the session-expired overlay. */
  onSignIn?: () => void;
}

function statusOf(error: unknown): number | undefined {
  return error instanceof ApiRequestError ? error.error.status : undefined;
}

function requestIdOf(error: unknown): string | undefined {
  return error instanceof ApiRequestError ? error.error.requestId : undefined;
}

export function NoAccess({ error, onRetry, onSignOut, onSignIn }: NoAccessProps) {
  const { t } = useTranslation();
  const status = statusOf(error);
  const requestId = requestIdOf(error);
  const forbidden = status === 403;
  const sessionExpired = status === 401;
  const network = status === 0;

  const title = forbidden
    ? t("auth.notStaffTitle")
    : sessionExpired
      ? t("auth.sessionExpiredTitle")
      : network
        ? t("state.networkError")
        : t("state.errorTitle");
  const body = forbidden
    ? t("auth.notStaff")
    : sessionExpired
      ? t("auth.sessionExpiredBody")
      : t("state.errorBody");

  return (
    <main className="flex min-h-dvh items-center justify-center bg-(--bg) px-4">
      <div
        role="alert"
        className="flex w-full max-w-sm flex-col items-center gap-5 rounded-(--radius-lg) border border-(--border) bg-(--surface) px-8 py-10 text-center shadow-(--shadow-lg)"
      >
        <span
          className="flex size-12 items-center justify-center rounded-full bg-(--danger-subtle) text-(--danger)"
          aria-hidden
        >
          <ShieldOff className="size-6" />
        </span>
        <div className="flex flex-col gap-1.5">
          <h1 className="text-(length:--text-lg) font-semibold text-(--text-strong)">{title}</h1>
          <p className="text-(length:--text-sm) text-(--text-muted)">{body}</p>
          {requestId && (
            <p className="tabular-mono text-(length:--text-2xs) text-(--text-subtle)">
              {t("state.requestId", { id: requestId })}
            </p>
          )}
        </div>

        <div className="flex w-full flex-col gap-2">
          {forbidden && onSignOut && (
            <Button variant="secondary" onClick={onSignOut} className="w-full">
              {t("auth.signInAnother")}
            </Button>
          )}
          {sessionExpired && (
            <Button
              // userInitiated: this is a user CLICKING "Sign in", not a renewal that failed. The flag is
              // what makes the provider show the overlay outright instead of dropping the click into a
              // silent recovery already in flight (a button that does nothing) or spending a refresh on
              // it (which would land the user back on this same card). renewalFailure null: no renewal
              // failed here, so there is no verdict to retry.
              onClick={
                onSignIn ??
                (() =>
                  signalSessionExpired({
                    tokenWasSent: false,
                    renewalFailure: null,
                    userInitiated: true,
                  }))
              }
              className="w-full"
            >
              {t("auth.signIn")}
            </Button>
          )}
          {!forbidden && !sessionExpired && onRetry && (
            <Button variant="secondary" onClick={onRetry} className="w-full">
              {t("action.retry")}
            </Button>
          )}
        </div>
      </div>
    </main>
  );
}
